/* eslint-disable @typescript-eslint/no-shadow */
import { useTheme } from '@mui/material/styles';
import {
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  FormHelperText,
  TextField,
  Typography,
  useMediaQuery,
} from '@mui/material';
import { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useMutation } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useSnackbar } from 'notistack';
import {
  InsertDriveFileOutlined as InsertDriveFileOutlinedIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import { createAuditorVisitReport } from '../api/createAuditorVisitReport';
import { getMessageFromError } from '@/core';
import { useAuditorVisitDetailContext } from '../contexts/AuditorVisitDetailContext';
import { FileUploader } from '@/common';
import { addFileToReport } from '../api/addFileToReport';

type AddReportFormType = {
  note: string;
  leaderNote?: {
    hours?: string;
    newStandard?: string;
    newIssue?: string;
  };
};

type AddReportModalProps = {
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
};

const AddReportModal = ({ isOpen, setIsOpen }: AddReportModalProps) => {
  const theme = useTheme();
  const [uploadedFiles, setUploadedFiles] = useState<FormData>();
  const [show, setShow] = useState(false);
  const [showAskModal, setShowAskModal] = useState(false);
  const fullScreen = useMediaQuery(theme.breakpoints.down('md'));
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const { auditorVisit, invalidateAuditorVisit } = useAuditorVisitDetailContext();

  const form = useForm<AddReportFormType>({
    mode: 'onTouched',
    defaultValues: {
      note: '',
      leaderNote: {
        hours: '',
        newStandard: '',
        newIssue: '',
      },
    },
  });

  const addReportMutation = useMutation({
    mutationFn: createAuditorVisitReport,
    onSuccess: (data) => {
      if (uploadedFiles) addFileToReport({ id: data.id, formData: uploadedFiles });
      enqueueSnackbar(
        t('Successfully added new report.', { defaultValue: 'Successfully added new report.' }),
        { variant: 'success' }
      );
      invalidateAuditorVisit();
      setIsOpen(false);
    },
    onError: (error: Error) => {
      enqueueSnackbar(getMessageFromError(error), { variant: 'error' });
    },
  });

  useEffect(() => {
    if (!isOpen) {
      form.reset();
      setUploadedFiles(undefined);
    }
  }, [isOpen, form]);

  const baseSubmit = (data: AddReportFormType) => {
    if (!uploadedFiles) {
      form.setError('root', {
        message: t('File is required', {
          defaultValue: 'File is required.',
        }).toString(),
      });
      return;
    }
    if (!data.leaderNote?.hours && !data.leaderNote?.newStandard && !data.leaderNote?.newIssue) {
      setShowAskModal(true);
      return;
    }

    if (data.leaderNote?.hours || data.leaderNote?.newStandard || data.leaderNote?.newIssue) {
      setShowAskModal(false);
    }

    const dataObj = {
      note: data.note,
      leaderNote:
        'Ali so bile izpolnjene vse ure? ' +
        data.leaderNote?.hours +
        '; Ali stranka razmišlja o novem standardu ali odhodu? ' +
        data.leaderNote?.newStandard +
        '; Ali je znano kdaj bo stranka naredila prehod na novo izdajo? ' +
        data.leaderNote?.newIssue,
    };

    addReportMutation.mutate({ visitId: auditorVisit.id, data: dataObj });
  };

  const handleClose = () => {
    form.reset();
    setIsOpen(false);
  };

  return (
    <>
      <Dialog
        fullScreen={fullScreen}
        open={showAskModal}
        aria-labelledby="responsive-dialog-title"
        fullWidth
      >
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
            <Box>
              <Typography variant="h6" component="p">
                {t('Were all the scheduled hours spent on the visit?', {
                  defaultValue: 'Were all the scheduled hours spent on the visit?',
                })}
              </Typography>
              <Controller
                control={form.control}
                name="leaderNote.hours"
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    multiline
                    rows={3}
                    error={!!form.formState.errors.leaderNote}
                    helperText={form.formState.errors.leaderNote?.message?.toString()}
                  />
                )}
              />
            </Box>
            <Box>
              <Typography variant="h6" component="p">
                {t('Is the client considering a new standard or departure?', {
                  defaultValue: 'Is the client considering a new standard or departure?',
                })}
              </Typography>
              <Controller
                control={form.control}
                name="leaderNote.newStandard"
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    multiline
                    rows={3}
                    error={!!form.formState.errors.leaderNote}
                    helperText={form.formState.errors.leaderNote?.message?.toString()}
                  />
                )}
              />
            </Box>
            <Box>
              <Typography variant="h6" component="p">
                {t('Is it known when the customer will make the transition to the new release?', {
                  defaultValue:
                    'Is it known when the customer will make the transition to the new release?',
                })}
              </Typography>
              <Controller
                control={form.control}
                name="leaderNote.newIssue"
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    multiline
                    rows={3}
                    error={!!form.formState.errors.leaderNote}
                    helperText={form.formState.errors.leaderNote?.message?.toString()}
                  />
                )}
              />
            </Box>
          </Box>
          <Button
            variant="contained"
            onClick={(e) => {
              e.preventDefault();
              form.handleSubmit(baseSubmit)();
            }}
            fullWidth
            sx={{ mt: '1rem' }}
          >
            {t('Submit', { defaultValue: 'Submit' })}
          </Button>
          {form.formState.errors?.root && (
            <FormHelperText error>{form.formState.errors.root.message}</FormHelperText>
          )}
        </DialogContent>
      </Dialog>
      <Dialog
        fullScreen={fullScreen}
        open={isOpen}
        onClose={handleClose}
        aria-labelledby="responsive-dialog-title"
        fullWidth
      >
        <DialogTitle id="responsive-dialog-title">
          {t('Add a new report', { defaultValue: 'Add a new report' })}
        </DialogTitle>
        <DialogContent>
          <Box
            component="form"
            onSubmit={form.handleSubmit(baseSubmit)}
            noValidate
            sx={{ mt: '1rem' }}
          >
            <Controller
              control={form.control}
              name="note"
              render={({ field }) => (
                <TextField
                  {...field}
                  label={t('Note', { defaultValue: 'Note' })}
                  fullWidth
                  multiline
                  rows={6}
                  error={!!form.formState.errors.note}
                  helperText={form.formState.errors.note?.message?.toString()}
                />
              )}
            />
            <Dialog
              fullScreen={fullScreen}
              open={show}
              onClose={() => setShow(false)}
              aria-labelledby="responsive-dialog-title"
              fullWidth
            >
              <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography variant="h5" component="p">
                  {t('Upload files', { defaultValue: 'Upload files' })}
                </Typography>
                <CloseIcon
                  sx={{ fontSize: '2rem', cursor: 'pointer' }}
                  onClick={() => setShow(false)}
                />
              </DialogTitle>
              <DialogContent>
                <FileUploader
                  // eslint-disable-next-line consistent-return
                  onFileUpload={async (file) => {
                    const formData = new FormData();
                    formData.append('title', file.file.name);
                    formData.append('attachments', file.file);
                    setUploadedFiles(formData);
                  }}
                  onUploadFinished={(allFilesSuccessfullyUploaded, newFiles) => {
                    if (allFilesSuccessfullyUploaded) {
                      form.clearErrors('root');
                      setShow(false);
                    }
                  }}
                />
              </DialogContent>
            </Dialog>

            {!uploadedFiles && (
              <Button
                variant="contained"
                type="button"
                fullWidth
                sx={{ mt: '1rem' }}
                onClick={() => setShow(true)}
              >
                {t('Upload report', { defaultValue: 'Upload report' })}
              </Button>
            )}
            {uploadedFiles && (
              <Box
                sx={(theme) => ({
                  backgroundColor: theme.palette.grey[200],
                  display: 'flex',
                  alignItems: 'center',
                  p: '1rem',
                  borderTopLeftRadius: `${theme.shape.borderRadius}px`,
                  borderBottomLeftRadius: `${theme.shape.borderRadius}px`,
                  position: 'relative',
                  mt: '1rem',
                })}
              >
                <Box
                  sx={(theme) => ({
                    position: 'absolute',
                    height: '100%',
                    backgroundColor: 'success.light',
                    width: '0.3rem',
                    left: 0,
                    top: 0,
                    borderTopLeftRadius: `${theme.shape.borderRadius}px`,
                    borderBottomLeftRadius: `${theme.shape.borderRadius}px`,
                  })}
                />
                <InsertDriveFileOutlinedIcon sx={{ fontSize: '2rem' }} />
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    ml: '1rem',
                  }}
                >
                  <Typography>
                    {uploadedFiles && uploadedFiles?.get('title')?.toString()}
                  </Typography>
                </Box>
              </Box>
            )}
            <Button variant="contained" type="submit" fullWidth sx={{ mt: '1rem' }}>
              {t('Submit', { defaultValue: 'Submit' })}
            </Button>
            {form.formState.errors?.root && (
              <FormHelperText error>{form.formState.errors.root.message}</FormHelperText>
            )}
            <Button variant="outlined" fullWidth sx={{ mt: '1rem' }} onClick={handleClose}>
              {t('Cancel', { defaultValue: 'Cancel' })}
            </Button>
          </Box>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default AddReportModal;
